import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Heart, ShieldCheck, Church, HeartHandshake, Palette, Store, Clapperboard, BookOpen, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { TextField, ConsentBox, Honeypot } from "@/components/form";
import { usePageMeta } from "@/hooks/usePageMeta";
import { api, apiError, fileUrl } from "@/lib/api";

const IMPACT = [
  { icon: Church, text: "Preserving places of worship" },
  { icon: HeartHandshake, text: "Strengthening ministries" },
  { icon: Palette, text: "Championing Christian artists" },
  { icon: Store, text: "Connecting Kingdom businesses" },
];

export default function Fund() {
  usePageMeta("Fuel the Ministry",
    "Support Lord Almighty with a one-time or monthly gift and help strengthen the body of Christ.");
  const [cfg, setCfg] = useState({ presets: { one_time: [], monthly: [] }, min_cents: 500, max_cents: 5000000, mode: "test" });
  const [frequency, setFrequency] = useState("one_time");
  const [amount, setAmount] = useState(5000);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ donor_name: "", donor_email: "", request_receipt: false, hp_field: "" });
  const [loading, setLoading] = useState(false);
  const [giving, setGiving] = useState({ goal: { enabled: false }, impact: {} });

  useEffect(() => {
    api.get("/payments/config").then((r) => {
      setCfg(r.data);
      setAmount(r.data.presets.one_time?.[1] || 5000);
    }).catch(() => {});
    api.get("/payments/giving-info").then((r) => setGiving(r.data)).catch(() => {});
  }, []);

  const presets = cfg.presets[frequency] || [];
  const effectiveCents = custom ? Math.round(parseFloat(custom) * 100) : amount;

  const pickFrequency = (f) => {
    setFrequency(f);
    setCustom("");
    setAmount((cfg.presets[f] || [])[1] || 5000);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.donor_name || !form.donor_email) return toast.error("Please add your name and email.");
    if (!effectiveCents || Number.isNaN(effectiveCents)) return toast.error("Please choose or enter a gift amount.");
    if (effectiveCents < cfg.min_cents || effectiveCents > cfg.max_cents)
      return toast.error(`Gift must be between $${(cfg.min_cents / 100).toFixed(0)} and $${(cfg.max_cents / 100).toLocaleString()}.`);
    setLoading(true);
    try {
      const { data } = await api.post("/payments/donate", {
        frequency, amount_cents: effectiveCents, donor_name: form.donor_name,
        donor_email: form.donor_email, request_receipt: form.request_receipt,
        origin_url: window.location.origin, hp_field: form.hp_field,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err));
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHero
        eyebrow="Fuel the Ministry"
        title="Your gift helps faith flourish"
        subtitle="Every gift to Lord Almighty helps preserve places of worship, strengthen ministries, champion Christian artists, and connect Kingdom businesses — so more lives can be reached with the love of Jesus."
        image="/feed5000.webp"
      />

      {/* JESUS UNITED STUDIO CAMPAIGN */}
      <section className="relative overflow-hidden bg-navy text-ivory">
        <div className="pointer-events-none absolute -top-24 right-[8%] h-72 w-72 rounded-full bg-gold/25 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-20 lg:py-24 grid gap-12 lg:grid-cols-2 items-center">
          <Reveal>
            <div className="rounded-3xl overflow-hidden shadow-[0_24px_70px_rgba(10,20,40,0.5)] ring-1 ring-gold/30">
              <img src="/ju-adam-eve.png" alt="Jesus United claymation scene of Adam and Eve in the garden" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-gold">
              <Clapperboard className="h-4 w-4" /> A Jesus United Studio Campaign
            </span>
            <h2 className="mt-3 font-display text-4xl lg:text-5xl font-semibold tracking-tight">
              Fuel the Bible Stories Studio
            </h2>
            <p className="mt-5 text-ivory/85 leading-relaxed">
              Jesus United is currently building the Gospel of John, chapter by chapter — at about
              <strong className="text-gold"> $2,500 per chapter</strong>. Our vision is to fund a studio
              of our own, so we can bring to life all the memorable stories of the Bible: from
              Adam &amp; Eve to Joseph, to Moses, to King David — and beyond.
            </p>

            <div className="mt-7 grid grid-cols-2 gap-4">
              <div className="rounded-2xl bg-white/10 border border-white/15 p-5">
                <BookOpen className="h-6 w-6 text-gold" />
                <p className="mt-2 font-display text-2xl font-semibold">$2,500</p>
                <p className="text-xs text-ivory/70">to produce each chapter</p>
              </div>
              <div className="rounded-2xl bg-white/10 border border-white/15 p-5">
                <Clapperboard className="h-6 w-6 text-gold" />
                <p className="mt-2 font-display text-2xl font-semibold">$300,000</p>
                <p className="text-xs text-ivory/70">studio campaign goal</p>
              </div>
            </div>

            <Button onClick={() => document.getElementById("give")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-8 rounded-full bg-gold hover:bg-gold-dark text-navy px-9 py-6 text-base shadow-2xl transition-transform hover:scale-105" data-testid="fund-studio-cta">
              Give Toward the Studio <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Reveal>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-transparent via-gold to-transparent" />
      </section>

      {/* GIVING GOAL THERMOMETER */}
      {giving.goal?.enabled && giving.goal?.target_cents > 0 && (
        <section className="mx-auto max-w-3xl px-5 lg:px-8 pt-14">
          <Reveal>
            <div className="rounded-2xl bg-white border border-border p-7 shadow-sm" data-testid="giving-goal">
              <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">Our current goal</p>
                  <h2 className="mt-1 font-display text-2xl text-navy font-semibold">{giving.goal.label || "Ministry Fund"}</h2>
                </div>
                <p className="font-display text-2xl text-emerald font-semibold">
                  ${(giving.goal.raised_cents / 100).toLocaleString()}
                  <span className="text-sm text-muted-foreground font-sans"> of ${(giving.goal.target_cents / 100).toLocaleString()}</span>
                </p>
              </div>
              <div className="mt-4 h-4 w-full rounded-full bg-ivory-alt overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-emerald to-gold"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, (giving.goal.raised_cents / giving.goal.target_cents) * 100)}%` }}
                  transition={{ duration: 1.1, ease: "easeOut" }}
                />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {Math.min(100, Math.round((giving.goal.raised_cents / giving.goal.target_cents) * 100))}% of the way there — thank you for helping us reach it.
              </p>
            </div>
          </Reveal>
        </section>
      )}

      <section id="give" className="mx-auto max-w-5xl px-5 lg:px-8 py-16 grid gap-10 lg:grid-cols-5">
        {/* Giving form */}
        <div className="lg:col-span-3">
          <Reveal>
            <form onSubmit={submit} className="rounded-2xl bg-white border border-border p-7 shadow-sm" data-testid="fund-form">
              <Honeypot value={form.hp_field} onChange={(v) => setForm({ ...form, hp_field: v })} />

              {/* Frequency */}
              <div className="grid grid-cols-2 gap-2 rounded-full bg-ivory-alt p-1">
                {[["one_time", "One-time"], ["monthly", "Monthly"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => pickFrequency(v)} data-testid={`freq-${v}`}
                    className={`rounded-full py-2.5 text-sm font-semibold transition-colors ${frequency === v ? "bg-emerald text-white shadow" : "text-navy/70"}`}>
                    {l}{v === "monthly" ? " gift" : ""}
                  </button>
                ))}
              </div>

              {/* Amounts */}
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {presets.map((c) => {
                  const active = !custom && amount === c;
                  return (
                    <button key={c} type="button" data-testid={`amount-${c}`}
                      onClick={() => { setAmount(c); setCustom(""); }}
                      className={`rounded-xl border py-3 font-display text-xl font-semibold transition-all ${active ? "border-emerald bg-emerald/10 text-emerald ring-2 ring-emerald/30" : "border-border bg-white text-navy hover:border-gold"}`}>
                      ${(c / 100).toLocaleString()}
                    </button>
                  );
                })}
              </div>

              {/* Custom */}
              <div className="mt-4">
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-navy/60 font-semibold">$</span>
                  <Input type="number" min="5" step="1" placeholder="Other amount" value={custom}
                    onChange={(e) => setCustom(e.target.value)} data-testid="amount-custom"
                    className="pl-8 bg-white border-border focus-visible:ring-emerald text-lg" />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {frequency === "monthly" ? "Charged monthly until you cancel." : "A single gift."} You'll complete payment securely on Stripe.
                </p>
              </div>

              {/* Donor details */}
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <TextField label="Your name" name="donor_name" required value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} />
                <TextField label="Email" name="donor_email" type="email" required value={form.donor_email} onChange={(e) => setForm({ ...form, donor_email: e.target.value })} hint="For your confirmation and receipt." />
              </div>

              <div className="mt-4">
                <ConsentBox name="request_receipt" checked={form.request_receipt} onChange={(v) => setForm({ ...form, request_receipt: v })}>
                  Please send me a donation receipt for tax purposes.
                </ConsentBox>
              </div>

              <Button type="submit" disabled={loading}
                className="mt-6 w-full rounded-full bg-emerald hover:bg-emerald-dark text-white py-6 text-base shadow-xl transition-transform hover:scale-[1.02]" data-testid="fund-give-btn">
                <Heart className="mr-2 h-5 w-5" />
                {loading ? "Redirecting to secure checkout…" :
                  `Give ${effectiveCents ? "$" + (effectiveCents / 100).toLocaleString() : ""}${frequency === "monthly" ? " / month" : ""}`}
              </Button>

              {cfg.mode === "test" && (
                <p className="mt-3 text-center text-xs text-gold-dark" data-testid="test-mode-note">
                  Test mode — use card 4242 4242 4242 4242, any future date & CVC. No real charge.
                </p>
              )}
            </form>
          </Reveal>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-2 space-y-5">
          <Reveal delay={0.1}>
            <div className="rounded-2xl bg-white border border-border p-6 shadow-sm">
              <h3 className="font-display text-xl text-navy font-semibold">Where your gift goes</h3>
              <ul className="mt-4 space-y-3">
                {IMPACT.map((it) => (
                  <li key={it.text} className="flex items-center gap-3 text-sm text-navy">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald/10 text-emerald"><it.icon className="h-4 w-4" /></span>
                    {it.text}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl bg-navy text-ivory p-6">
              <ShieldCheck className="h-7 w-7 text-gold" />
              <p className="mt-3 text-sm text-ivory/85 leading-relaxed">
                Lord Almighty is a Florida 501(c)(3) tax-exempt organization. Gifts may be tax-deductible
                as allowed by law, and we're glad to provide a donation receipt on request. Payments are
                processed securely by Stripe — we never see or store your card details.
              </p>
            </div>
          </Reveal>
        </aside>
      </section>

      {/* IMPACT STORY (owner-editable, shown only when provided) */}
      {(giving.impact?.body || giving.impact?.image_url) && (
        <section className="bg-ivory-alt" data-testid="impact-section">
          <div className="mx-auto max-w-6xl px-5 lg:px-8 py-20 grid gap-10 lg:grid-cols-2 items-center">
            {giving.impact?.image_url && (
              <Reveal>
                <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(27,42,71,0.15)]">
                  <img src={fileUrl(giving.impact.image_url)} alt="How gifts are helping" className="w-full h-[360px] object-cover" />
                </div>
              </Reveal>
            )}
            <Reveal delay={0.1}>
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.22em] text-gold-dark">Your gift at work</span>
              <h2 className="mt-3 font-display text-4xl font-semibold text-navy tracking-tight">
                {giving.impact?.heading || "How your gifts are helping"}
              </h2>
              <p className="mt-5 text-muted-foreground leading-relaxed whitespace-pre-line">{giving.impact?.body}</p>
            </Reveal>
          </div>
        </section>
      )}
    </div>
  );
}
